import { motion } from 'framer-motion';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import * as actionTypes from '../constantes/actionTypes';
import SpringsMob from '../assets/Images/mobile/Springs.png';
import SpringsTab from '../assets/Images/tablet/Springs.png';
import { getMicrositePrivacyCheckboxCount, hasMicrositePrivacy } from '../constantes/micrositePrivacy';
import i18n from '../i18nextInit';
import { CliniqueState, StepView } from '../type';
import { trackPlausibleEvent } from '../services/analytics';
import { BackgroundImage } from '../styles/onBoardStyle';
import Checkbox from '../views/customPrivacyPolicy/checkbox';
import MicrositeApacPP from '../views/customPrivacyPolicy/micrositeApac';
import MicrositeKoreanPP from '../views/customPrivacyPolicy/micrositeKorean';
import MicrositeUsPP from '../views/customPrivacyPolicy/micrositeUs';

const ShadematchPrivacyPolicy = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const isMicrosite = useSelector((state: CliniqueState) => state.session.isMicrosite);
	const [isFirstChecked, setFirstChecked] = useState(false);
	const [isScndChecked, setScndChecked] = useState(false);
	const hasMicrositePrivacyPolicy = hasMicrositePrivacy(isMicrosite, i18n.language);
	const micrositeCheckboxCount = getMicrositePrivacyCheckboxCount(i18n.language);
	const isButtonEnabled = hasMicrositePrivacyPolicy
		? micrositeCheckboxCount === 0 || (isFirstChecked && (micrositeCheckboxCount === 1 || isScndChecked))
		: isFirstChecked;

	const handleAgree = () => {
		if (!isButtonEnabled) {
			return;
		}

		trackPlausibleEvent('sm_intro_completed');
		trackPlausibleEvent('sm_terms_accepted');
		dispatch({ type: actionTypes.SWITCH_STEP, value: StepView.tuto });
	};

	return (
		<Component initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
			<BackgroundImage src={window.innerWidth > 768 ? SpringsTab : SpringsMob} />
			<Content>
				<Title>{hasMicrositePrivacyPolicy ? t('microsite-privacy-title') : t('shadematch-privacy-title')}</Title>
				{hasMicrositePrivacyPolicy && i18n.language === 'EnUs' ? (
					<MicrositeUsPP isFirstChecked={isFirstChecked} isScndChecked={isScndChecked} checkFirst={setFirstChecked} checkScnd={setScndChecked} checkboxColor='#1a1a1a' />
				) : hasMicrositePrivacyPolicy && i18n.language === 'KoKr' ? (
					<MicrositeKoreanPP isFirstChecked={isFirstChecked} isScndChecked={isScndChecked} checkFirst={setFirstChecked} checkScnd={setScndChecked} checkboxColor='#1a1a1a' />
				) : hasMicrositePrivacyPolicy ? (
					<MicrositeApacPP isFirstChecked={isFirstChecked} checkFirst={setFirstChecked} requiresConsent={micrositeCheckboxCount === 1} checkboxColor='#1a1a1a' />
				) : (
					<>
						<Text dangerouslySetInnerHTML={{ __html: t('shadematch-privacy-introduction') }} />
						<Consent>
							<Checkbox checked={isFirstChecked} color='#1a1a1a' onChange={(event) => setFirstChecked(event.target.checked)} />
							<Text as='span' dangerouslySetInnerHTML={{ __html: t('shadematch-privacy-facial-geometry-consent') }} />
						</Consent>
					</>
				)}
				<Button $isEnabled={isButtonEnabled} disabled={!isButtonEnabled} whileTap={isButtonEnabled ? { scale: 1.03 } : undefined} onClick={handleAgree}>
					{hasMicrositePrivacyPolicy ? t('microsite-privacy-cta') : t('cta-OK')}
				</Button>
			</Content>
		</Component>
	);
};

const Component = styled(motion.div)`
	min-height: 100vh;
	color: #1a1a1a;
	box-sizing: border-box;
	padding: 112px 24px 96px;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const Content = styled.div`
	width: min(100%, 640px);
	display: flex;
	flex-direction: column;
	gap: 24px;
`;

const Title = styled.h1`
	margin: 0;
	font: normal normal 700 32px/120% 'Clinique Helvetica Neue';
`;

const Text = styled.p`
	margin: 0;
	font: normal normal 400 16px/150% 'Clinique Helvetica Neue';

	a {
		color: #1a1a1a;
		text-decoration: underline;
	}
`;

const Consent = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 12px;
`;

const Button = styled(motion.button)<{ $isEnabled: boolean }>`
	appearance: none;
	border: 1px solid ${(props) => (props.$isEnabled ? '#1a1a1a' : '#c7c7c7')};
	background: ${(props) => (props.$isEnabled ? '#1a1a1a' : '#f2f2f2')};
	color: ${(props) => (props.$isEnabled ? '#fff' : '#8a8a8a')};
	width: 100%;
	padding: 18px 24px;
	font: normal normal 500 16px/100% 'Clinique Helvetica Neue';
	transition: background 0.3s ease, color 0.3s ease;
`;

export default ShadematchPrivacyPolicy;
